import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PlusCircle, ArrowRight, Eye } from "lucide-react";
import { adminDashboard, apiError } from "@/lib/adminApi";
import { AdminSettingsPanel } from "@/pages/admin/AdminLayout";
import { AdminPickRow } from "@/pages/admin/AdminPickRow";

function Stat({ label, value, tone = "white", testid }) {
  const tones = { green: "text-[#00E676]", red: "text-[#EF4444]", white: "text-white", gold: "text-[#F59E0B]" };
  return (
    <div className="card-mp p-5" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className={`mt-2 font-mono text-2xl font-bold tracking-tight sm:text-3xl ${tones[tone]}`}>{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-dashboard"], queryFn: adminDashboard, retry: 1 });
  if (isLoading) return <p className="font-mono text-xs text-slate-500" data-testid="admin-dashboard-loading">Loading...</p>;
  if (isError || !data) {
    return (
      <div className="rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 p-5" data-testid="admin-dashboard-error">
        <p className="font-display text-sm font-bold uppercase tracking-wider text-[#EF4444]">Dashboard failed to load</p>
        <p className="mt-1 font-mono text-xs text-slate-400">{apiError(error)}</p>
        <button onClick={() => refetch()} className="btn-primary mt-4 !px-4 !py-2" data-testid="admin-dashboard-retry">Retry</button>
      </div>
    );
  }
  const { counts, stats } = data;
  const vipPreview = data.vip_test_mode === "vip";

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Control Room</p>
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">Dashboard</h1>
        </div>
        <Link to="/admin/picks/new" className="btn-primary !px-5 !py-2.5" data-testid="admin-dashboard-create-btn">
          <PlusCircle size={15} /> Create Pick
        </Link>
      </div>

      <div className={`mt-6 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm ${vipPreview ? "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]" : "border-[#1E2738] bg-[#0F131D] text-slate-400"}`} data-testid="admin-preview-banner">
        <Eye size={15} />
        <span>Customer preview: <b className="text-white">{vipPreview ? "VIP MEMBER" : "NON-MEMBER"}</b> — {vipPreview ? "your browser sees unlocked VIP picks and analysis. No real membership is granted; visitors are unaffected." : "your browser sees the public site exactly as a free visitor does."}</span>
        <Link to="/picks" target="_blank" className="ml-auto font-mono text-[11px] uppercase tracking-wider underline-offset-2 hover:underline" data-testid="admin-preview-open-link">Open preview ↗</Link>
      </div>

      {data.data_mode === "demo" && (
        <div className="mt-6 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-4 py-3 text-sm text-[#F59E0B]" data-testid="admin-demo-mode-banner">
          Public site is in <b>DEMO</b> mode — visitors see the labelled demo ledger ({counts.demo_picks} demo picks). Switch to <b>Live</b> to show only real Money Printer picks.
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Drafts" value={counts.drafts} testid="admin-stat-drafts" />
        <Stat label="Pending (Published)" value={counts.pending} tone="gold" testid="admin-stat-pending" />
        <Stat label="Settled" value={counts.settled} testid="admin-stat-settled" />
        <Stat label="Live Record" value={stats.record} tone="green" testid="admin-stat-record" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Net Units" value={`${stats.net_units > 0 ? "+" : ""}${stats.net_units}U`} tone={stats.net_units >= 0 ? "green" : "red"} testid="admin-stat-units" />
        <Stat label="ROI" value={`${stats.roi > 0 ? "+" : ""}${stats.roi}%`} tone={stats.roi >= 0 ? "green" : "red"} testid="admin-stat-roi" />
        <Stat label="Win %" value={`${stats.win_pct}%`} testid="admin-stat-winpct" />
        <Stat label="Last 10" value={stats.last10} testid="admin-stat-last10" />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="card-mp p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Recent Activity</h3>
            <Link to="/admin/picks" className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-slate-400 hover:text-[#00E676]">All picks <ArrowRight size={12} /></Link>
          </div>
          <div className="mt-4 space-y-2">
            {data.recent.length === 0 && <p className="py-8 text-center text-sm text-slate-500" data-testid="admin-recent-empty">No real picks yet. Create your first pick to get started.</p>}
            {data.recent.map((p) => <AdminPickRow key={p.id} pick={p} compact />)}
          </div>
        </div>
        <div className="lg:hidden"><AdminSettingsPanel /></div>
        <div className="card-mp p-5" data-testid="admin-whop-status">
          <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Whop Membership</h3>
          <ul className="mt-4 space-y-2 font-mono text-[11px] uppercase tracking-wider">
            {[
              ["Login (OAuth)", data.whop.login_configured],
              ["Webhook secret", data.whop.webhook_configured],
              ["VIP checkout URL", data.whop.checkout_url_set],
              ["Discord invite", data.whop.discord_invite_set],
            ].map(([label, ok]) => (
              <li key={label} className="flex items-center justify-between">
                <span className="text-slate-400">{label}</span>
                <span className={ok ? "text-[#00E676]" : "text-[#F59E0B]"}>{ok ? "Connected" : "Not set"}</span>
              </li>
            ))}
            <li className="flex items-center justify-between border-t border-[#1E2738] pt-2">
              <span className="text-slate-400">Members / VIP</span>
              <span className="text-white" data-testid="admin-whop-members">{data.whop.members} / {data.whop.vip_members}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
